import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { CsvImportStatus } from '@/lib/csv-import/api';
import { isApiError } from '@/lib/errors';
import { logger } from '@/lib/logger';
import {
  type CoreMapping,
  type MappingAction,
  type MappingProposal,
  reviewRowKey,
} from '@/lib/vendor-import';

import {
  type CreateImportResult,
  describeConfirmError,
  describeHubspotImportError,
  getHubspotImportObjectTypes,
  getHubspotMappingProposals,
  type HubspotImportJobStatus,
  startHubspotImport,
  waitForHubspotImportJob,
} from './api';

interface HubspotImportWizardState {
  selectedTypes: string[];
  proposalsByEntity: Record<string, MappingProposal[]> | null;
  proposalsLoadError: boolean;
  proposalsForbidden: boolean;
  truncatedByEntity: Record<string, boolean> | null;
  sampledByEntity: Record<string, boolean> | null;
  totalRecordsByEntity: Record<string, number> | null;
  coreMappingsByEntity: Record<string, CoreMapping[]> | null;
  actionByKey: Record<string, MappingAction>;
  jobStatus: HubspotImportJobStatus | null;
  confirmError: string | null;
}

export interface UseHubspotImportWizardControllerParams {
  orgId: number;
  isOpen: boolean;
  connected: boolean;
  onClose: () => void;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
}

/**
 * Owns all state, effects, and network handlers for HubspotImportWizard.
 * Kept separate from the component so the wizard file stays a thin render +
 * step-config layer over this controller.
 */
export function useHubspotImportWizardController({
  orgId,
  isOpen,
  connected,
  onClose,
  onImportCreated,
}: UseHubspotImportWizardControllerParams) {
  const {
    data: objectTypesData,
    isPending: objectTypesPending,
    isError: objectTypesError,
    error: objectTypesErrorObj,
    refetch: refetchObjectTypes,
  } = useQuery({
    queryKey: ['hubspotImportObjectTypes', orgId],
    queryFn: ({ signal }) => getHubspotImportObjectTypes(orgId, signal),
    enabled: isOpen && connected,
  });
  const objectTypes = useMemo(
    () => objectTypesData?.object_types ?? [],
    [objectTypesData]
  );
  // A 403 on object-types means the workspace lacks the import entitlement.
  // Surface the admin-guidance state instead of a misleading retry affordance.
  const objectTypesForbidden =
    isApiError(objectTypesErrorObj) && objectTypesErrorObj.isForbidden();

  // Component state
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [proposalsByEntity, setProposalsByEntity] = useState<Record<
    string,
    MappingProposal[]
  > | null>(null);
  const [proposalsLoadError, setProposalsLoadError] = useState(false);
  const [proposalsForbidden, setProposalsForbidden] = useState(false);
  const [truncatedByEntity, setTruncatedByEntity] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [sampledByEntity, setSampledByEntity] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [totalRecordsByEntity, setTotalRecordsByEntity] = useState<Record<
    string,
    number
  > | null>(null);
  const [coreMappingsByEntity, setCoreMappingsByEntity] = useState<Record<
    string,
    CoreMapping[]
  > | null>(null);
  const [actionByKey, setActionByKey] = useState<Record<string, MappingAction>>(
    {}
  );
  const [jobStatus, setJobStatus] = useState<HubspotImportJobStatus | null>(
    null
  );
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Seed selectedTypes to all names once per session. Tracked with a ref so an
  // intentional empty selection (user unchecks the last type) is not re-seeded;
  // reset() clears the ref so a reopened wizard seeds again.
  const didSeedTypesRef = useRef(false);
  useEffect(() => {
    if (!didSeedTypesRef.current && objectTypes.length > 0) {
      didSeedTypesRef.current = true;
      setSelectedTypes(objectTypes.map(t => t.name));
    }
  }, [objectTypes]);

  // Keep a ref current each render so render delegates / canAdvance / onBeforeNext
  // closures read the latest state without capturing it in their dep arrays.
  const stateRef = useRef<HubspotImportWizardState>({
    selectedTypes,
    proposalsByEntity,
    proposalsLoadError,
    proposalsForbidden,
    truncatedByEntity,
    sampledByEntity,
    totalRecordsByEntity,
    coreMappingsByEntity,
    actionByKey,
    jobStatus,
    confirmError,
  });
  stateRef.current = {
    selectedTypes,
    proposalsByEntity,
    proposalsLoadError,
    proposalsForbidden,
    truncatedByEntity,
    sampledByEntity,
    totalRecordsByEntity,
    coreMappingsByEntity,
    actionByKey,
    jobStatus,
    confirmError,
  };

  // Tracks the active AbortController so the cleanup effect can abort on unmount.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    didSeedTypesRef.current = false;
    setSelectedTypes([]);
    setProposalsByEntity(null);
    setProposalsLoadError(false);
    setProposalsForbidden(false);
    setTruncatedByEntity(null);
    setSampledByEntity(null);
    setTotalRecordsByEntity(null);
    setCoreMappingsByEntity(null);
    setActionByKey({});
    setJobStatus(null);
    setConfirmError(null);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleCancelPoll = useCallback(() => {
    controllerRef.current?.abort();
    handleClose();
  }, [handleClose]);

  const handleJobStatus = useCallback((status: HubspotImportJobStatus) => {
    setJobStatus(status);
  }, []);

  const handleTypesChange = useCallback((names: string[]) => {
    setSelectedTypes(names);
  }, []);

  const handleActionChange = useCallback(
    (key: string, action: MappingAction) => {
      setActionByKey(prev => ({ ...prev, [key]: action }));
    },
    []
  );

  const handleRetryObjectTypes = useCallback(() => {
    void refetchObjectTypes();
  }, [refetchObjectTypes]);

  // isDirty: true when selection differs from the loaded all-set OR proposals loaded.
  const isDirty = useMemo(() => {
    if (proposalsByEntity !== null) {
      return true;
    }
    if (objectTypes.length === 0) {
      return false;
    }
    const allNames = new Set(objectTypes.map(t => t.name));
    if (selectedTypes.length !== allNames.size) {
      return true;
    }
    return selectedTypes.some(t => !allNames.has(t));
  }, [selectedTypes, objectTypes, proposalsByEntity]);

  const handleConfigureBeforeNext = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setProposalsLoadError(false);
    try {
      const res = await getHubspotMappingProposals(
        orgId,
        stateRef.current.selectedTypes,
        controller.signal
      );
      // A newer request may have superseded this one while it was in flight.
      // Applying these (now stale) results would clobber the newer proposals,
      // so drop them — the stale-check only running in catch was the bug.
      if (controllerRef.current !== controller) {
        logger.debug('HubSpot mapping proposals: skipped stale response', {
          orgId,
        });
        return;
      }
      const initial: Record<string, MappingAction> = {};
      for (const proposals of Object.values(res.proposals)) {
        for (const proposal of proposals) {
          initial[reviewRowKey(proposal.entity_type_name, proposal.raw_key)] =
            proposal.proposed_action;
        }
      }
      setProposalsByEntity(res.proposals);
      setTruncatedByEntity(res.truncated);
      setSampledByEntity(res.sampled);
      setTotalRecordsByEntity(res.total_records);
      setCoreMappingsByEntity(res.core_mappings ?? null);
      setActionByKey(initial);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw err;
      }
      if (controllerRef.current !== controller) {
        logger.debug('HubSpot mapping proposals: skipped stale response', {
          orgId,
        });
        return;
      }
      // A 403 means the workspace isn't entitled to import. Surface admin
      // guidance on the Configure step and stop — don't advance to Review with
      // a generic load error the user can't act on.
      if (isApiError(err) && err.isForbidden()) {
        setProposalsForbidden(true);
        throw err;
      }
      logger.warn('HubSpot mapping proposals failed', {
        orgId,
        statusCode: isApiError(err) ? err.statusCode : undefined,
        error: err,
      });
      setProposalsLoadError(true);
      // Still advance to review (user can proceed without proposals)
    }
  }, [orgId]);

  const handleConfirm = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setConfirmError(null);
    let jobId: number | null = null;
    try {
      const { proposalsByEntity, proposalsLoadError, actionByKey } =
        stateRef.current;
      const confirmedMappings =
        proposalsLoadError ||
        !proposalsByEntity ||
        Object.keys(proposalsByEntity).length === 0
          ? undefined
          : Object.values(proposalsByEntity)
              .flat()
              .map(proposal => ({
                raw_key: proposal.raw_key,
                entity_type_name: proposal.entity_type_name,
                action:
                  actionByKey[
                    reviewRowKey(proposal.entity_type_name, proposal.raw_key)
                  ] ?? proposal.proposed_action,
                target_property_name: proposal.target_property_name,
                field_type_name: proposal.field_type_name,
                is_multi: proposal.is_multi,
              }));

      const { job_id } = await startHubspotImport(
        orgId,
        {
          selectedObjectTypes: stateRef.current.selectedTypes,
          ...(confirmedMappings ? { confirmedMappings } : {}),
        },
        controller.signal
      );
      jobId = job_id;
      const job = await waitForHubspotImportJob(
        orgId,
        job_id,
        controller.signal,
        handleJobStatus
      );
      if (job.csv_import_id === null) {
        throw new Error('HubSpot import finished without an import id');
      }
      const result = job.result as unknown as CreateImportResult | null;
      if (result?.warning) {
        toast.warning(result.warning);
      }
      onImportCreated(job.csv_import_id, result?.status ?? 'analyzing');
      handleClose();
    } catch (err) {
      controller.abort();
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      if (controllerRef.current !== controller) {
        logger.debug('HubSpot import confirm: skipped stale response', {
          orgId,
        });
        return;
      }
      if (isApiError(err) && err.isForbidden()) {
        setConfirmError(describeHubspotImportError(err));
        throw err;
      }
      logger.warn('HubSpot import create failed', {
        orgId,
        jobId,
        statusCode: isApiError(err) ? err.statusCode : undefined,
        error: err,
      });
      toast.error(describeConfirmError(err));
      throw err;
    } finally {
      if (controllerRef.current === controller) {
        setJobStatus(null);
      }
    }
  }, [orgId, onImportCreated, handleClose, handleJobStatus]);

  return {
    objectTypes,
    objectTypesPending,
    objectTypesError,
    objectTypesForbidden,
    stateRef,
    isDirty,
    handleOpenChange,
    handleCancelPoll,
    handleTypesChange,
    handleActionChange,
    handleRetryObjectTypes,
    handleConfigureBeforeNext,
    handleConfirm,
  };
}
